import React, { useMemo, useState, useRef, useEffect } from 'react';
import { PiCaretLeftLight, PiCaretRightLight, PiCaretDownLight, PiFileLight, PiCalendarLight, PiPencilSimpleLineLight, PiBriefcaseLight } from 'react-icons/pi';
import { FileProps, FolderProps, MatterInfo } from './types';
import { formatDocumentName, formatBytes, formatDate } from '../../../utils/functions';
import Badge from '../../shared/TailwindComponents/Badge';
import { Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FileNavigatorProps {
  files: FileProps[];
  currentFileId: string;
  onFileChange: (fileId: string) => void;
  folders?: FolderProps[];
  onFolderChange?: (folderId: string) => void;
  fileType?: string;
  fileSize?: number;
  createdAt?: string;
  modifiedAt?: string;
  matterInfo?: MatterInfo | null;
}

const getFileExtension = (fileName: string): string => {
  const ext = fileName.split('.').pop() || '';
  return ext;
};

const FileNavigator: React.FC<FileNavigatorProps> = ({
  files,
  currentFileId,
  onFileChange,
  folders = [],
  onFolderChange,
  fileType,
  fileSize,
  createdAt,
  modifiedAt,
  matterInfo
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoverTimer, setHoverTimer] = useState<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const currentIndex = useMemo(() => {
    return files.findIndex((f) => f.FileID === currentFileId);
  }, [files, currentFileId]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1;
  const hasMultiple = files.length > 1 || folders.length > 0;

  const handlePrevious = () => {
    if (hasPrevious) {
      onFileChange(files[currentIndex - 1].FileID);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onFileChange(files[currentIndex + 1].FileID);
    }
  };

  const handleMouseEnter = () => {
    if (!hasMultiple) return;
    const timer = setTimeout(() => {
      setShowDropdown(true);
    }, 350);
    setHoverTimer(timer);
  };

  const handleMouseLeave = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
    setShowDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const currentFile = files[currentIndex];

  return (
    <div
      className="flex items-center gap-2 px-4 py-1.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 relative flex-shrink-0"
      onMouseLeave={handleMouseLeave}
    >
      {/* Previous button */}
      {hasMultiple && (
        <button
          onClick={handlePrevious}
          disabled={!hasPrevious}
          className={`p-1 rounded transition-colors ${
            hasPrevious
              ? 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          }`}
          title={t("Previous file")}
        >
          <PiCaretLeftLight className="w-3.5 h-3.5" />
        </button>
      )}

      {/* File info with hover trigger */}
      <div
        className={`flex-1 min-w-0 flex items-center gap-2 ${hasMultiple ? 'cursor-pointer' : ''}`}
        onMouseEnter={handleMouseEnter}
        onClick={() => hasMultiple && setShowDropdown(!showDropdown)}
      >
        {currentFile && (
          <>
            <span className="text-sm text-gray-900 dark:text-white truncate hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              {(() => {
                const { name } = formatDocumentName(currentFile.FileName);
                return name;
              })()}
            </span>
            {matterInfo ? (
              <Tooltip
                title={
                  <div className="max-w-xs">
                    <div className="font-semibold text-sm mb-1">{matterInfo.title}</div>
                    {matterInfo.description && (
                      <div className="text-xs text-gray-200 mb-1.5 line-clamp-3">{matterInfo.description}</div>
                    )}
                    {matterInfo.clients && matterInfo.clients.length > 0 && (
                      <div className="text-xs text-gray-300 border-t border-gray-500 pt-1 mt-1">
                        {matterInfo.clients.map(c => c.name).filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div className="text-[10px] text-gray-400 mt-1">Click to open matter</div>
                  </div>
                }
                arrow
                placement="bottom"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(`/matters/${matterInfo.uuid}`);
                  }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors cursor-pointer"
                >
                  <PiBriefcaseLight className="w-3 h-3" />
                  <span className="truncate max-w-[140px]">{matterInfo.title}</span>
                </button>
              </Tooltip>
            ) : (
              <Badge color='gray' className='text-[11px]'>
                {getFileExtension(currentFile.FileName)}
              </Badge>
            )}
          </>
        )}
        {!currentFile && (
          <span className="text-sm text-gray-500 dark:text-gray-400">{t('No file selected')}</span>
        )}

        {/* File details - right side */}
        <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
          {fileType && (
            <Badge color='gray' className='text-[11px] flex items-center gap-1'>
              <PiFileLight className="w-3 h-3" />
              {fileType}
            </Badge>
          )}
          {fileSize != null && fileSize > 0 && (
            <Badge color='gray' className='text-[11px]' tooltip={t('File Size')}>
              {formatBytes(fileSize)}
            </Badge>
          )}
          {createdAt && (
            <Badge color='gray' className='text-[11px] flex items-center gap-1' tooltip={t('Created')}>
              <PiCalendarLight className="w-3 h-3" />
              {formatDate(createdAt)}
            </Badge>
          )}
          {modifiedAt && modifiedAt !== createdAt && (
            <Badge color='gray' className='text-[11px] flex items-center gap-1' tooltip={t('Modified')}>
              <PiPencilSimpleLineLight className="w-3 h-3" />
              {formatDate(modifiedAt)}
            </Badge>
          )}
          {hasMultiple && (
            <>
              <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-1">
                {currentFile ? `${currentIndex + 1} / ${files.length}` : `${files.length} files`}
              </span>
              <PiCaretDownLight className={`w-3 h-3 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
            </>
          )}
        </div>
      </div>

      {/* Next button */}
      {hasMultiple && (
        <button
          onClick={handleNext}
          disabled={!hasNext}
          className={`p-1 rounded transition-colors ${
            hasNext
              ? 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          }`}
          title={t("Next file")}
        >
          <PiCaretRightLight className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Dropdown menu with smooth animation */}
      <div
        ref={dropdownRef}
        className={`absolute top-full left-3 right-3 mt-0.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto transition-all duration-200 origin-top ${
          showDropdown
            ? 'opacity-100 scale-y-100 translate-y-0'
            : 'opacity-0 scale-y-95 -translate-y-1 pointer-events-none'
        }`}
        onMouseEnter={handleMouseEnter}
      >
        {/* Files section */}
        {files.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-[10px] text-gray-500 dark:text-gray-400 tracking-wider border-b border-gray-100 dark:border-gray-600">
              {t('Files')} ({files.length})
            </div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar-main py-1">
              {files.map((file) => {
                const { name } = formatDocumentName(file.FileName);
                const ext = getFileExtension(file.FileName);
                return (
                  <button
                    key={file.FileID}
                    onClick={() => {
                      onFileChange(file.FileID);
                      setShowDropdown(false);
                    }}
                    className={`w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 transition-colors ${
                      file.FileID === currentFileId
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] rounded bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {ext}
                    </span>
                    <span className="truncate">{name}</span>
                    {file.FileSize != null && file.FileSize > 0 && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto flex-shrink-0">
                        {formatBytes(file.FileSize)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Folders section */}
        {folders.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-[10px] text-gray-500 dark:text-gray-400 tracking-wider border-t border-gray-100 dark:border-gray-600">
              {t('Folders')} ({folders.length})
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              {folders.map((folder) => (
                <button
                  key={folder.FolderID}
                  onClick={() => {
                    if (onFolderChange) {
                      onFolderChange(folder.FolderID);
                    }
                    setShowDropdown(false);
                  }}
                  className="w-full px-3 py-1.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  <span className="truncate">{folder.FolderName}</span>
                  {folder.FileCount > 0 && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto flex-shrink-0">
                      {folder.FileCount} {t('files')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileNavigator;
